import { isDemoUser, getCurrentUser, isUserSubmission } from '../utils/auth';
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, where, deleteDoc, doc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

function DataTable({ db, onEdit }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMySubmissions, setShowMySubmissions] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const auth = getAuth();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', { 
        user: user?.email,
        isMobile: window.innerWidth < 768 
      });
      setCurrentUser(user);
    });
    
    return () => unsubscribe();
  }, [auth]);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const submissionsRef = collection(db, 'submissions');
      let q = query(submissionsRef);
      
      console.log('Detailed Auth Debug:', {
        auth: auth,
        currentUser: auth.currentUser,
        email: auth.currentUser?.email,
        isMobile: window.innerWidth < 768,
        showMySubmissions
      });
      
      if (showMySubmissions) {
        const userEmail = currentUser?.email;
        console.log('Filtering by email:', userEmail);
        if (userEmail) {
          q = query(submissionsRef, where('email', '==', userEmail));
        }
      }

      const querySnapshot = await getDocs(q);
      console.log('Found submissions:', querySnapshot.size);
      // Log raw submissions data
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('Raw submission data:', {
          id: doc.id,
          userId: data.userId,
          email: data.email,
          address: data.address,
          submittedBy: data.submittedBy,  // In case it's stored differently
          allFields: data  // Show all fields
        });
      });

      const submissionsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Submission Debug:', {
          id: doc.id,
          userId: data.userId,
          email: data.email,
          address: data.address,
          isCurrentUser: isUserSubmission(data, getCurrentUser())
        });
        return {
          id: doc.id,
          ...doc.data(),
          canEdit: !isDemoUser() && data.email === auth.currentUser?.email
        };
      });

      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error fetching submissions:', error, {
        authState: auth.currentUser ? 'logged in' : 'not logged in',
        email: auth.currentUser?.email
      });
    } finally {
      setLoading(false);
    }
  }, [db, showMySubmissions, auth]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDelete = async (submissionId) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await deleteDoc(doc(db, 'submissions', submissionId));
        await fetchSubmissions();
      } catch (error) {
        console.error('Error deleting submission:', error);
      }
    }
  };

  const filteredAndSortedSubmissions = React.useMemo(() => {
    let result = [...submissions];

    // Apply search term
    if (searchTerm) {
      result = result.filter(item => 
        item.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.borough?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.landlordName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        result = result.filter(item => 
          String(item[key]).toLowerCase().includes(filters[key].toLowerCase())
        );
      }
    });

    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [submissions, filters, sortConfig, searchTerm]);

  return (
    <div className="mx-auto bg-white shadow-md overflow-hidden max-w-[98vw] md:max-w-[95vw] lg:max-w-[90vw] mt-6">
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">Submissions</h2>
        
        {/* Search and Filter Section */}
        <div className="mb-4 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-grow">
              <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by address, borough, or landlord..."
                className="w-full px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center px-3 py-2 border rounded-md hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Advanced Filters
              </button>

              {/* My Submissions Toggle */}
              <label className="inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox"
                  checked={showMySubmissions}
                  onChange={(e) => setShowMySubmissions(e.target.checked)}
                  className="sr-only peer" 
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">My Submissions</span>
              </label>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="p-4 border rounded-lg bg-gray-50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[
                { key: 'borough', label: 'Borough' },
                { key: 'address', label: 'Address' },
                { key: 'unitNumber', label: 'Unit' },
                { key: 'currentRent', label: 'Rent', type: 'number' },
                { key: 'initialRent', label: 'Initial Rent', type: 'number' },
                { key: 'bedrooms', label: 'Bedrooms' },
                { key: 'moveInDate', label: 'Move-in Date' },
                { key: 'unitsInBuilding', label: 'Units in Building', type: 'number' },
                { key: 'landlordName', label: 'Landlord/Management' },
                { key: 'landlordRating', label: 'Rating', type: 'number' }
              ].map(({ key, label, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    type={type || "text"}
                    placeholder={`Filter by ${label.toLowerCase()}`}
                    value={filters[key] || ''}
                    onChange={(e) => handleFilter(key, e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Results Counter */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {filteredAndSortedSubmissions.length} of {submissions.length} submissions
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    'Borough',
                    'Address',
                    'Unit',
                    'Rent',
                    'Initial Rent',
                    'Bedrooms',
                    'Move-in Date',
                    'Units in Building',
                    'Landlord/Management',
                    'Rating',
                    'Actions'
                  ].map((header) => (
                    <th
                      key={header}
                      onClick={() => handleSort(header.toLowerCase().replace(/[\s/]+/g, ''))}
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{header}</span>
                        {sortConfig.key === header.toLowerCase().replace(/[\s/]+/g, '') ? (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        ) : (
                          <span className="opacity-0 group-hover:opacity-100">↓</span>
                        )}
                        {header !== 'Actions' && (
                          <button className="ml-1 opacity-0 group-hover:opacity-100">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{submission.borough}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{submission.address}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{submission.unitNumber || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      ${submission.currentRent.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {submission.initialRent ? `$${submission.initialRent.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {submission.bedrooms === '0' ? 'Studio' : submission.bedrooms}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {submission.moveInMonth && submission.moveInYear 
                        ? `${submission.moveInMonth}/${submission.moveInYear}`
                        : '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {submission.unitsInBuilding || '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{submission.landlordName}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {submission.landlordRating || '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
  {submission.canEdit && (
    <div className="flex space-x-2">
      {!isDemoUser() && (
        <>
          <button
            onClick={() => onEdit(submission)}
            className="text-blue-600 hover:text-blue-900 text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(submission.id)}
            className="text-red-600 hover:text-red-900 text-sm"
          >
            Delete
          </button>
        </>
      )}
    </div>
  )}
</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataTable; 